import { useEffect, useState, useCallback } from "react";
import { fromColumnVisibilityModel, toHiddenColumnRecord, userPreferencesStorage } from "../service/userPreferenceStorageService";

/**
 * Hook to manage DataGrid column visibility state with persistent user preferences.
 *
 * - Loads visibility from storage based on page key.
 * - Always enforces required default columns.
 * - Updates storage whenever the user changes visibility.
 * - Prevents redundant saves if visibility hasn’t changed.
 *
 * @param pageKey unique identifier for the current grid/page
 * @param requiredDefaults columns that must always be visible (and their default values)
 */
export const useColumnVisibility = (
    pageKey: string,
    requiredDefaults: Record<string, boolean>,
    enforceRequiredDefaults: boolean = false
) => {
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<Record<string, boolean>>(requiredDefaults);

    // Load stored preferences on mount
    useEffect(() => {
        const pagePrefs = userPreferencesStorage.getPagePreference(pageKey);

        const storedVisibility =
        pagePrefs?.gridSettings?.hiddenColumns?.length > 0
            ? toHiddenColumnRecord(pagePrefs.gridSettings.hiddenColumns)
            : {};

        let mergedModel = {
            ...requiredDefaults,
            ...storedVisibility,
        };
        if (enforceRequiredDefaults) {
            // Remove only keys where requiredDefaults[key] === true
            mergedModel = {
                ...storedVisibility,
                ...requiredDefaults
            }
        }

        // Merge stored + required defaults (defaults always win)


        setColumnVisibilityModel(mergedModel);
    }, [pageKey]);

    // Handle visibility changes (user toggles column)
    const handleVisibilityChange = useCallback(
        (visibilityModel: Record<string, boolean>) => {
        setColumnVisibilityModel(visibilityModel);

        const hiddenColumns = fromColumnVisibilityModel(visibilityModel);

        // Get current storage state
        const currentPrefs = userPreferencesStorage.getPagePreference(pageKey);

        // Only save if changed
        const previousHidden = currentPrefs?.gridSettings?.hiddenColumns || [];
        const hasChanged =
            JSON.stringify(hiddenColumns) !== JSON.stringify(previousHidden);

        if (hasChanged) {
            userPreferencesStorage.updatePagePreference(pageKey, {
                gridSettings: { hiddenColumns }
            });
        }
        },
        [pageKey]
    );

    return {
        columnVisibilityModel,
        handleVisibilityChange,
    };
};
